(() => {
  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)');

    const benefitStrip = root.querySelector('[data-dm-benefits]');
    if (benefitStrip) {
      const messages = [...benefitStrip.querySelectorAll('[data-dm-benefit]')];
      const toggle = benefitStrip.querySelector('[data-dm-benefit-toggle]');
      let index = 0;
      let paused = false;
      toggle.addEventListener('click', () => {
        paused = !paused;
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.querySelector('span').textContent = paused ? '▶' : 'Ⅱ';
      });
      const timer = window.setInterval(() => {
        if (!root.isConnected) { window.clearInterval(timer); return; }
        if (paused || document.hidden || reduced?.matches || benefitStrip.matches(':hover, :focus-within')) return;
        const rect = benefitStrip.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        messages[index].hidden = true;
        index = (index + 1) % messages.length;
        messages[index].hidden = false;
      }, 3000);
    }

    const videos = [...root.querySelectorAll('[data-dm-video]')];

    const loadVideo = video => {
      const source = video.querySelector('source[data-src]');
      if (source) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        video.load();
      }
    };

    const autoAllowed = () =>
      !(reduced && reduced.matches) &&
      !(navigator.connection && navigator.connection.saveData);

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const video = entry.target;

            if (
              entry.isIntersecting &&
              entry.intersectionRatio >= .2 &&
              autoAllowed() &&
              video.dataset.pausedByUser !== 'true'
            ) {
              loadVideo(video);
              video.play().catch(() => {});
            } else if (
              !entry.isIntersecting ||
              entry.intersectionRatio < .2
            ) {
              video.pause();
            }
          });
        }, { threshold: [0, .2] })
      : null;

    videos.forEach(video => {
      const label = video.getAttribute('aria-label') || 'video';

      const sync = () => {
        video.setAttribute(
          'aria-label',
          (video.paused ? 'Play ' : 'Pause ') + label
        );
        video.setAttribute('aria-pressed', String(!video.paused));
      };

      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);

      const toggle = () => {
        if (video.paused) {
          video.dataset.pausedByUser = 'false';
          loadVideo(video);
          video.play().catch(sync);
        } else {
          video.dataset.pausedByUser = 'true';
          video.pause();
        }
      };

      video.addEventListener('click', toggle);
      video.addEventListener('keydown', event => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          toggle();
        }
      });

      sync();
      if (observer) observer.observe(video);
    });

    document.addEventListener('visibilitychange', () => {
      videos.forEach(video => {
        if (document.hidden) {
          video.pause();
        } else if (
          autoAllowed() &&
          video.dataset.pausedByUser !== 'true'
        ) {
          const rect = video.getBoundingClientRect();
          const visible = Math.max(
            0,
            Math.min(rect.bottom, window.innerHeight) -
            Math.max(rect.top, 0)
          );

          if (visible >= rect.height * .2) {
            loadVideo(video);
            video.play().catch(() => {});
          }
        }
      });
    });

    if (!observer && autoAllowed()) {
      videos.forEach(video => {
        loadVideo(video);
        video.play().catch(() => {});
      });
    }

    const reviews = root.querySelector('[data-dm-reviews]');

    if (reviews) {
      const cards = [...reviews.querySelectorAll('.dm-review')];
      const dots = [...root.querySelectorAll('[data-dm-review-dot]')];

      const moveReview = index => reviews.scrollTo({
        left: cards[index].offsetLeft - cards[0].offsetLeft,
        behavior: reduced && reduced.matches ? 'auto' : 'smooth'
      });

      dots.forEach((dot, index) =>
        dot.addEventListener('click', () => moveReview(index))
      );

      reviews.addEventListener('scroll', () => {
        let nearest = 0;

        cards.forEach((card, index) => {
          if (
            Math.abs(
              card.offsetLeft - cards[0].offsetLeft - reviews.scrollLeft
            ) <
            Math.abs(
              cards[nearest].offsetLeft -
              cards[0].offsetLeft -
              reviews.scrollLeft
            )
          ) {
            nearest = index;
          }
        });

        dots.forEach((dot, index) =>
          dot.setAttribute('aria-pressed', String(index === nearest))
        );
      }, { passive: true });

      reviews.addEventListener('keydown', event => {
        if (
          window.matchMedia('(max-width:749px)').matches &&
          (event.key === 'ArrowRight' || event.key === 'ArrowLeft')
        ) {
          event.preventDefault();

          const current = Math.max(
            0,
            dots.findIndex(dot =>
              dot.getAttribute('aria-pressed') === 'true'
            )
          );

          moveReview(Math.max(
            0,
            Math.min(
              cards.length - 1,
              current + (event.key === 'ArrowRight' ? 1 : -1)
            )
          ));
        }
      });
    }

    const carousel = root.querySelector('[data-dm-carousel]');

    if (carousel) {
      const move = direction => carousel.scrollBy({
        left: direction * (
          carousel.querySelector('.dm-clip').getBoundingClientRect().width + 20
        ),
        behavior: reduced && reduced.matches ? 'auto' : 'smooth'
      });

      root.querySelector('[data-dm-prev]')
        .addEventListener('click', () => move(-1));

      root.querySelector('[data-dm-next]')
        .addEventListener('click', () => move(1));

      carousel.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          move(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
    }

    const sticky = root.querySelector('.dm-sticky');
    const primaryButtons = [...root.querySelectorAll('.dm-hero-action > .dm-button, .dm-purchase button[type="submit"]')];
    const syncSticky = () => {
      const heroButton = primaryButtons[0];
      if (!sticky || !heroButton) return;
      const inView = el => {
        const rect = el.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0;
      };
      sticky.hidden = primaryButtons.some(inView);
    };
    if ('IntersectionObserver' in window) {
      const ctaObserver = new IntersectionObserver(syncSticky, {threshold: [0, 1]});
      primaryButtons.forEach(button => ctaObserver.observe(button));
    }
    window.addEventListener('scroll', syncSticky, {passive:true});
    window.addEventListener('resize', syncSticky);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', syncSticky);
    if ('ResizeObserver' in window) new ResizeObserver(syncSticky).observe(root);
    syncSticky();

    const form = root.querySelector('[data-dm-form]');
    const status = root.querySelector('[data-dm-status]');
    const currency = root.dataset.currency || 'INR';

    const format = cents => new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(cents / 100);

    const selectedSlot = () => form.querySelector('input[name="dm-slot"]:checked');
    const slotSummary = root.querySelector('[data-dm-slot-summary]');
    const stickyAction = root.querySelector('[data-dm-sticky-action]');
    const stickyLabel = root.querySelector('[data-dm-sticky-label]');
    if (stickyAction) stickyAction.addEventListener('click', event => {
      if (!selectedSlot()) return;
      event.preventDefault();
      form.requestSubmit();
    });
    const hasKit = () =>
      form.elements['dm-option'].value === 'kit';

    form.addEventListener('change', () => {
      const slot = selectedSlot();
      if (stickyLabel) stickyLabel.textContent = slot ? root.dataset.addLabel : root.dataset.chooseLabel;
      if (slot) slotSummary.textContent = 'Your class time: ' + slot.dataset.label + ' · Monday–Friday';
      const compareTotal = Number(root.dataset.memberCompare) + (hasKit() ? Number(root.dataset.kitCompare) : 0);
      root.querySelectorAll('[data-dm-compare]').forEach(el => el.textContent = format(compareTotal));
      const total = Number(root.dataset.memberPrice) +
        (hasKit() ? Number(root.dataset.kitPrice) : 0);

      root.querySelectorAll('[data-dm-total]')
        .forEach(el => el.textContent = format(total));
    });

    form.addEventListener('submit', async event => {
      event.preventDefault();

      if (
        root.dataset.ready !== 'true' ||
        !root.dataset.memberId
      ) return;

      const button = form.querySelector('button[type=submit]');
      if (button.disabled) return;

      const slot = selectedSlot();
      if (!slot || !['06:00','07:00','08:00','17:00','18:00','19:00'].includes(slot.value)) {
        status.textContent = 'Please choose your class time.';
        form.querySelector('input[name="dm-slot"]').focus();
        return;
      }

      const items = [{
        id: Number(root.dataset.memberId),
        quantity: 1,
        properties: {
          'Membership': '6 months, one-time payment',
          'Class time': slot.dataset.label,
          '_dm_slot': slot.value,
          '_dm_timezone': 'Asia/Kolkata',
          'Starter Kit selected': hasKit() ? 'Yes' : 'No'
        }
      }];

      if (hasKit()) {
        if (!root.dataset.kitId) {
          status.textContent =
            'The Starter Kit is currently unavailable. Please select membership only.';
          return;
        }

        items.push({
          id: Number(root.dataset.kitId),
          quantity: 1
        });
      }

      button.disabled = true;
      if (stickyAction) stickyAction.setAttribute('aria-disabled', 'true');
      if (stickyLabel) stickyLabel.textContent = 'Adding…';
      status.textContent = 'Adding your selection…';

      try {
        const response = await fetch(root.dataset.addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            typeof result.description === 'string'
              ? result.description
              : 'Could not add your selection. Please try again.'
          );
        }

        window.location.assign(root.dataset.cartUrl);
      } catch (error) {
        status.textContent = error.message ||
          'Could not add your selection. Please try again.';
        button.disabled = false;
        if (stickyAction) stickyAction.removeAttribute('aria-disabled');
        if (stickyLabel) stickyLabel.textContent = root.dataset.addLabel;
      }
    });
  }

  document.querySelectorAll('[data-dm]').forEach(init);

  document.addEventListener('shopify:section:load', event =>
    event.target.querySelectorAll('[data-dm]').forEach(init)
  );
})();

